'use client';

/**
 * Help registry — the source of truth for "what UI affordances are visible
 * on screen right now?". Lives outside React so multiple providers can
 * subscribe without prop drilling, and reading is synchronous (no renders
 * triggered just to snapshot the list).
 *
 * Design:
 *   - <HelpHint> components call `register()` on mount and `unregister()`
 *     on unmount.
 *   - The widget's `userContext` provider calls `snapshot()` at the moment
 *     of sending a turn — it never subscribes, so hints flipping in and
 *     out doesn't re-render the widget.
 *   - IDs are stable per mount; the consumer can rely on them for dedup
 *     when two copies of the same hint exist (tabs + headers).
 */

export interface HelpHintEntry {
  id: string;
  label: string;
  description?: string;
}

type Listener = () => void;

class HelpRegistryImpl {
  private hints = new Map<symbol, HelpHintEntry>();
  private listeners = new Set<Listener>();

  register(entry: HelpHintEntry): symbol {
    const key = Symbol(entry.id);
    this.hints.set(key, entry);
    this.emit();
    return key;
  }

  unregister(key: symbol): void {
    if (this.hints.delete(key)) this.emit();
  }

  update(key: symbol, entry: HelpHintEntry): void {
    const prev = this.hints.get(key);
    if (!prev) return;
    if (prev.label === entry.label && prev.description === entry.description && prev.id === entry.id) {
      return;
    }
    this.hints.set(key, entry);
    this.emit();
  }

  /**
   * Snapshot the currently-visible hints. De-duplicates by `id`, keeping
   * the most-recently registered entry — handy when a component rerenders
   * with slightly different labels (e.g. i18n switch mid-session).
   */
  snapshot(): HelpHintEntry[] {
    const byId = new Map<string, HelpHintEntry>();
    for (const entry of this.hints.values()) {
      byId.set(entry.id, entry);
    }
    return [...byId.values()];
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    for (const fn of this.listeners) fn();
  }
}

// Module-level singleton. The widget is mounted once per tab; hints are
// naturally scoped to the same tab.
export const helpRegistry = new HelpRegistryImpl();
